/**
 * Feature: Auth (Phase 3)
 * Scope: Supabase Auth, Sign up, Sign in, Sign out, Session Persistence, User Profile.
 * Status: Phase 3 Implementation
 *
 * This module provides typed helper functions that wrap the existing
 * Supabase client instance (src/lib/supabase/client.ts).
 *
 * The database trigger public.handle_new_user() automatically creates
 * a public.profiles row when a new Supabase Auth user is inserted into
 * auth.users. Sign-up should pass username and display_name through
 * options.data so the trigger can bootstrap the profile.
 */

import { supabase } from '@/lib/supabase/client';
import type { Session, User } from '@supabase/supabase-js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SignUpData {
  email: string;
  password: string;
  username: string;
  display_name?: string;
}

export interface SignInData {
  email: string;
  password: string;
}

export interface AuthResult<T> {
  data: T | null;
  error: Error | null;
}

export interface AuthSession {
  session: Session | null;
  user: User | null;
}

export type SignUpResult = AuthResult<AuthSession>;
export type SignInResult = AuthResult<AuthSession>;
export type SignOutResult = AuthResult<null>;
export type GetSessionResult = AuthResult<AuthSession>;
export type GetUserResult = AuthResult<{ user: User | null }>;

export type AuthStateChangeListener = (session: AuthSession) => void;

// ---------------------------------------------------------------------------
// Sign up
// ---------------------------------------------------------------------------

/**
 * Creates a new Supabase Auth user.
 *
 * The database trigger `public.handle_new_user()` will automatically create
 * a `public.profiles` row using `raw_user_meta_data` (username, display_name).
 * Do NOT manually insert into public.profiles.
 */
export async function signUp(data: SignUpData): Promise<SignUpResult> {
  const { email, password, username, display_name } = data;

  const redirectTo =
    typeof window !== 'undefined' ? window.location.origin : undefined;

  const { data: authData, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: redirectTo,
      data: {
        username,
        ...(display_name ? { display_name } : {}),
      },
    },
  });

  if (error) {
    return { data: null, error };
  }

  return {
    data: { session: authData.session, user: authData.user },
    error: null,
  };
}

// ---------------------------------------------------------------------------
// Sign in
// ---------------------------------------------------------------------------

export async function signIn(data: SignInData): Promise<SignInResult> {
  const { email, password } = data;

  const { data: authData, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    return { data: null, error };
  }

  return {
    data: { session: authData.session, user: authData.user },
    error: null,
  };
}

// ---------------------------------------------------------------------------
// Sign out
// ---------------------------------------------------------------------------

export async function signOut(): Promise<SignOutResult> {
  const { error } = await supabase.auth.signOut();

  if (error) {
    return { data: null, error };
  }

  return { data: null, error: null };
}

// ---------------------------------------------------------------------------
// Get session
// ---------------------------------------------------------------------------

export async function getSession(): Promise<GetSessionResult> {
  const { data, error } = await supabase.auth.getSession();

  if (error) {
    return { data: null, error };
  }

  const user = await supabase.auth.getUser();

  return {
    data: { session: data.session, user: user.data.user },
    error: null,
  };
}

// ---------------------------------------------------------------------------
// Get user
// ---------------------------------------------------------------------------

export async function getUser(): Promise<GetUserResult> {
  const { data, error } = await supabase.auth.getUser();

  if (error) {
    return { data: null, error };
  }

  return { data: { user: data.user }, error: null };
}

// ---------------------------------------------------------------------------
// Auth state change listener
// ---------------------------------------------------------------------------

/**
 * Subscribe to authentication state changes.
 * Returns an unsubscribe function that detaches the listener.
 */
export function onAuthStateChange(callback: AuthStateChangeListener): () => void {
  const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
    callback({ session, user: session?.user ?? null });
  });

  return () => {
    subscription.unsubscribe();
  };
}
